import { ReactElement, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDelayNegate } from '../../../../utils/hooks/useDelayNegate';
import { addMessage, clearMessages } from '../../../../store/ancillary/actions';
import StyleAuxiliary from '../../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import Card, { Actions } from '../../../UI/MDC/Card/Card';
import { Headline4, Subtitle2 } from '../../../UI/MDC/Typography/Typography';

import { CSS_CLASSES } from './_constants';
import classes from './Confirm.module.scss';

/**
 * Functional component to confirm request submissions.
 * Raises 'Success' message banner, renders card with text, footer action buttons for navigation.
 * @param {Object} props Properties passed to component.
 * @returns {ReactElement} Rendered component.
 */
const Confirm = ({ hideDelay = null, ...props }) => {
	const dispatch = useDispatch();
	let delayedHide = useDelayNegate(hideDelay ? false : true, hideDelay ?? 0);
	//#region Lifecycle Hooks
	/**
	 * Effect on component mount. Clears existing message banners and raises message banner of type 'Success'.
	 * @returns {Function} Effect cleanup performed on dismount. Clears message banner raised on mount.
	 */
	useEffect(() => {
		dispatch(clearMessages());
		dispatch(
			addMessage({
				Key: 5,
				Value: props.bannerMessage
					? props.bannerMessage
					: 'Request(s) successfully submitted.',
			})
		);
		return () => dispatch(clearMessages());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Lifecycle hook on local variable change. Clears raised messages if content hidden after delay.
	 * @returns void.
	 */
	useEffect(() => {
		if (hideDelay && delayedHide !== false) {
			dispatch(clearMessages());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [delayedHide, hideDelay]);
	//#endregion

	return hideDelay && delayedHide ? null : (
		<StyleAuxiliary
			classes={classnames([
				classes[CSS_CLASSES.CONTAINER],
				props.className,
			])}
		>
			<Card medium>
				<Headline4>Request Submission Confirmation</Headline4>
				{props.bodyMessage && (
					<Subtitle2>{props.bodyMessage}</Subtitle2>
				)}
				{props.children}
				{props.actionButtons && (
					<Actions className={classes[CSS_CLASSES.ACTIONS]}>
						{props.actionButtons}
					</Actions>
				)}
			</Card>
		</StyleAuxiliary>
	);
};

Confirm.propTypes = {
	actionButtons: PropTypes.node,
	bannerMessage: PropTypes.string,
	bodyMessage: PropTypes.string,
	children: PropTypes.node,
	hideDelay: PropTypes.number,
};

export default Confirm;
